import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { CarrierProps, ToolScope } from "./types.js";
import { annotationsFor } from "./tool-annotations.js";
import { denyUnlessScoped } from "./scope-guard.js";
import { generateStorefrontLogo } from "./lib/storefront-logo.js";

export const STOREFRONT_LOGO_TOOL_SCOPES: Record<string, ToolScope> = {
  generate_storefront_logo: "read",
};

type TextResult = { content: Array<{ type: "text"; text: string }>; isError?: boolean };
const ok = (text: string): TextResult => ({ content: [{ type: "text", text }] });
const err = (text: string): TextResult => ({ isError: true, content: [{ type: "text", text }] });

/**
 * Available to every authenticated Carrier MCP client (Cursor, Claude, CLI).
 * Always returns a unique SVG mark. Adds a PNG when the Carrier worker (or
 * the local process) has an OpenAI image key — developers do not need one.
 *
 * `props` is required for the call-time scope check, mirroring the Worker copy.
 * On this surface index.ts hardcodes every scope, so the guard cannot currently
 * refuse anybody; it is a regression fence for the day stdio derives scope from
 * an API key or a login.
 */
export function registerStorefrontLogoTools(
  server: McpServer,
  env: Record<string, string | undefined> = {},
  props: CarrierProps,
): void {
  server.registerTool(
    "generate_storefront_logo",
    {
      title: "Generate Storefront Logo",
      description:
        "Generate a storefront logo (SVG always; PNG when an image key is configured on the Carrier worker). Works for any authenticated Carrier MCP user — no local image-API key required. Write the SVG to public/brand/logo.svg in the storefront.",
      inputSchema: {
        name: z.string().min(1).describe("Brand / shop name, e.g. Bananas"),
        accent: z
          .string()
          .regex(/^#?[0-9A-Fa-f]{6}$/)
          .optional()
          .describe("Accent hex, e.g. #FF8A00"),
        tagline: z.string().optional().describe("Optional tagline for AI-enhanced marks"),
      },
      annotations: annotationsFor("generate_storefront_logo", "read"),
    },
    async ({ name, accent, tagline }) => {
      const denied = denyUnlessScoped(props, "generate_storefront_logo", STOREFRONT_LOGO_TOOL_SCOPES);
      if (denied) return denied;
      try {
        const hex = accent ? (accent.startsWith("#") ? accent : `#${accent}`) : undefined;
        const logo = await generateStorefrontLogo({
          name,
          accent: hex,
          tagline,
          env: { ...env, ...process.env },
        });
        return ok(
          JSON.stringify({
            brand: name,
            source: logo.source,
            filename: "public/brand/logo.svg",
            png_filename: logo.pngBase64 ? "public/brand/logo.png" : null,
            svg: logo.svg,
            png_base64: logo.pngBase64 ?? null,
            next: "Write svg to the storefront public/brand/logo.svg (and png if present). Nav and icons read that path.",
          }),
        );
      } catch (e) {
        return err(e instanceof Error ? e.message : "logo generation failed");
      }
    },
  );
}
